import dayjs from 'dayjs'
import { find } from 'lodash-es'
import { DictionaryItem, Direction, Flight, Gateway } from '../../../bewotec/openapi'
import { handleError, handleWarning } from '../../../general/errorHandling'
import {
  DEFAULT_DATE_FORMAT, DEFAULT_TIME_FORMAT, ISO_DATE_FORMAT, convertFormat,
  getValidDateFormat
} from '../../../general/helper'
import { ICruise, IPaxConfig } from '../../../store/catalog/interfaces'
import { IDetailCruise } from '../cruise/interfaces'
import { mapAirportDataForFlight, mapFlightData } from '../flight/helper'
import { IAirline, IAirportData, IFlightClass, ITwoWayFlightSegments } from '../flight/interfaces'
import { DisplayFunctionFlightPrice } from '../price/interfaces'
import { FlightClass } from './../interfaces'
import {
  IExpinetFlexFlightType,
  IExpinetFlightType,
  IFilterKey,
  IFlightDisplay,
  IMappedGateway,
  IOutboundInboundFlexFlight,
} from './interfaces'

export const hasFlexFlight = (catalogCruises: ICruise[], cruise: IDetailCruise) => {
  const catalogCruise = find(catalogCruises, catCruise => {
    return catCruise.id === cruise.id
  })

  return !!catalogCruise?.hasFlexFlight
}

export const initFlights = (bewoFlights: Flight[]) => {
  const flights: IExpinetFlexFlightType[] = []

  for (const flight of bewoFlights) {
    switch (flight.direction) {
      case Direction.O:
      case Direction.R:
        flexFlightHelper.makeOneWayFlexFlight(flight, flights)
        break
      case Direction.RT:
        flexFlightHelper.makeTwoWayFlexFlight(flight, flights)
        break
    }
  }

  return flights
}

const makeOneWayFlexFlight = (flight: Flight, flights: IExpinetFlexFlightType[]) => {
  if (!flight.flightSegments?.length) {
    flexFlightHelper.AddError('makeOneWayFlexFlight')
    return
  }

  const first = flight.flightSegments.at(0)!
  const last = flight.flightSegments.at(-1)!

  const departure = mapAirportDataForFlight(
    !!first.publish,
    first.depAirportCode,
    first.depAirportName,
    first.depDate,
    first.depTime
  )

  const arrival = mapAirportDataForFlight(
    !!last.publish,
    last.arrAirportCode,
    last.arrAirportName,
    last.arrDate,
    last.arrTime
  )

  const mainFlight = mapFlightData(flight.direction === Direction.O ? last : first, flight.direction)

  let stopArrival: IAirportData | undefined
  let stopDeparture: IAirportData | undefined
  let feederFlight = null

  if (first.carrierNumber !== last.carrierNumber) {
    stopArrival = mapAirportDataForFlight(
      !!first.publish,
      first.arrAirportCode,
      first.arrAirportName,
      first.arrDate,
      first.arrTime
    )

    stopDeparture = mapAirportDataForFlight(
      !!last.publish,
      last.depAirportCode,
      last.depAirportName,
      last.depDate,
      last.depTime
    )

    feederFlight = mapFlightData(flight.direction === Direction.O ? first : last, flight.direction)
  }

  const airlines = getAirlineArray(flight, flight.direction)

  const flightClass: IFlightClass = {
    id: flight.flightClassCode as FlightClass,
    name: flight.flightClassName ?? '',
  }

  const f: IExpinetFlexFlightType = {
    id: flight.code ?? '',
    price: !!flight.pricings
      ? flight.pricings.reduce((accumulator, pricing) => accumulator + (pricing.amount ?? 0), 0)
      : 0,
    direction: flight.direction,
  }
  const g: IOutboundInboundFlexFlight = {
    airlines,
    flightClass,
    mainFlight,
    feederFlight,
    arrival,
    departure,
    stopArrival,
    stopDeparture,
  }

  switch (flight.direction) {
    case Direction.O:
      f.outboundFlight = g
      break
    case Direction.R:
      f.inboundFlight = g
      break
  }

  flights.push(f)
}

const makeTwoWayFlexFlight = (flight: Flight, flights: IExpinetFlexFlightType[]) => {
  if (!flight.flightSegments?.length) {
    flexFlightHelper.AddError('makeTwoWayFlexFlight - no flight segments')
    return
  }

  const first: ITwoWayFlightSegments = {
    out: flight.flightSegments.at(0)!,
    in: null,
  }

  const last: ITwoWayFlightSegments = {
    out: null,
    in: flight.flightSegments.at(-1)!,
  }

  for (let i = 0; i < flight.flightSegments.length; ++i) {
    if (flight.flightSegments[i].direction === Direction.R) {
      first.in = flight.flightSegments[i]
      last.out = flight.flightSegments[i - 1]
      break
    }
  }

  {
    if (!first.out || !last.out || !first.in || !last.in) {
      flexFlightHelper.AddError('makeTwoWayFlexFlight')
      return
    }

    const departure = mapAirportDataForFlight(
      true,
      first.out.depAirportCode,
      first.out.depAirportName,
      first.out.depDate,
      first.out.depTime
    )

    const arrival = mapAirportDataForFlight(
      true,
      last.out.arrAirportCode,
      last.out.arrAirportName,
      last.out.depDate,
      last.out.arrTime
    )

    const mainFlight = mapFlightData(last.out, Direction.O)

    let stopArrival: IAirportData | undefined
    let stopDeparture: IAirportData | undefined
    let feederFlight = null

    if (first.out.carrierNumber !== last.out.carrierNumber) {
      stopDeparture = mapAirportDataForFlight(
        true,
        last.out.depAirportCode,
        last.out.depAirportName,
        last.out.depDate,
        last.out.depTime
      )

      stopArrival = mapAirportDataForFlight(
        true,
        first.out.arrAirportCode,
        first.out.arrAirportName,
        first.out.depDate,
        first.out.arrTime
      )

      feederFlight = mapFlightData(first.out, Direction.O)
    }

    const airlines = getAirlineArray(flight, Direction.O)

    const flightClass: IFlightClass = {
      id: flight.flightClassCode as FlightClass,
      name: flight.flightClassName ?? '',
    }

    const f: IExpinetFlexFlightType = {
      id: flight.code ?? '',
      direction: flight.direction,
      price: !!flight.pricings
        ? flight.pricings.reduce((accumulator, pricing) => accumulator + (pricing.amount ?? 0), 0)
        : 0,
      outboundFlight: {
        airlines,
        flightClass,
        departure,
        arrival,
        stopArrival,
        stopDeparture,
        mainFlight,
        feederFlight,
      },
      inboundFlight: undefined,
    }

    flights.push(f)
  }

  {
    const departure = mapAirportDataForFlight(
      true,
      first.in.depAirportCode,
      first.in.depAirportName,
      first.in.depDate,
      first.in.depTime
    )

    const arrival = mapAirportDataForFlight(
      true,
      last.in.arrAirportCode,
      last.in.arrAirportName,
      last.in.arrDate,
      last.in.arrTime
    )

    const mainFlight = mapFlightData(first.in, Direction.R)

    let stopArrival: IAirportData | undefined
    let stopDeparture: IAirportData | undefined
    let feederFlight = null

    if (first.in.carrierNumber !== last.in.carrierNumber) {
      stopDeparture = mapAirportDataForFlight(
        true,
        last.in.depAirportCode,
        last.in.depAirportName,
        last.in.depDate,
        last.in.depTime
      )

      stopArrival = mapAirportDataForFlight(
        true,
        first.in.arrAirportCode,
        first.in.arrAirportName,
        first.in.arrDate,
        first.in.arrTime
      )

      feederFlight = mapFlightData(last.in, Direction.R)
    }

    const airlines = getAirlineArray(flight, Direction.R)

    const flightClass: IFlightClass = {
      id: flight.flightClassCode as FlightClass,
      name: flight.flightClassName ?? '',
    }

    const f: IOutboundInboundFlexFlight = {
      airlines,
      flightClass,
      departure,
      arrival,
      stopArrival,
      stopDeparture,
      mainFlight,
      feederFlight,
    }

    const lastFlight = flights.at(-1)

    if (!lastFlight) {
      handleWarning('Flight is null', {
        fileName: 'details/flex-flight/helper.ts',
        functionName: 'makeTwoWayFlight',
      })
      return
    }

    lastFlight.inboundFlight = f
  }
}

export const createFlightDisplay = (
  flight: IExpinetFlexFlightType,
  displayFlightPrice: DisplayFunctionFlightPrice
): IFlightDisplay => {
  let display = ''

  if (!!flight.outboundFlight) {
    const outboundFlight = flight.outboundFlight
    const stops = !!outboundFlight.stopDeparture ? 'mit Umstieg' : 'Direktflug'
    const airlineNames = getAirlineNames(outboundFlight)

    display += `${airlineNames} (${stops})`

    if (!!outboundFlight.departure) {
      const arrivalTime = outboundFlight.departure.time
      const formattedTime = convertFormat(arrivalTime, getValidDateFormat(arrivalTime), DEFAULT_TIME_FORMAT)

      display += ` ${formattedTime}`
    }
  }

  if (!!flight.outboundFlight && !!flight.inboundFlight) {
    display += ' - '
  }

  if (!!flight.inboundFlight) {
    const inboundFlight = flight.inboundFlight
    const stops = 'stopArrival' in inboundFlight && !!inboundFlight.stopArrival ? 'mit Umstieg' : 'Direktflug'
    const airlineNames = getAirlineNames(inboundFlight)

    display += `${airlineNames} (${stops})`

    if (!!inboundFlight.departure) {
      const departureTime = inboundFlight.departure.time
      const formattedTime = convertFormat(departureTime, getValidDateFormat(departureTime), DEFAULT_TIME_FORMAT)

      display += ` ${formattedTime}`
    }
  }

  return {
    listDisplay: display,
    id: flight.id,
    price: displayFlightPrice(flight),
  }
}

export const findFilterKey = (selectedFilter: string | undefined | null, filterItems: IFilterKey[]) => {
  if (!selectedFilter) return '0'

  // i = 1 since the first element is a placeholder
  for (let i = 1; i < filterItems.length; i++) {
    const item = filterItems[i]

    if (item.id && valuesAreEqual(item.id, selectedFilter)) {
      return String(i)
    }
  }

  return '0'
}

const valuesAreEqual = (f: string, g: string) => {
  if (!f || !g) return false

  return f === g
}

export const gatewayId = (selectedFlight: IExpinetFlexFlightType | null) => {
  if (!selectedFlight) return null

  const inboundFlight = selectedFlight.inboundFlight
  const outboundFlight = selectedFlight.outboundFlight

  switch (selectedFlight.direction) {
    case Direction.RT:
    case Direction.O:
      return !!outboundFlight ? outboundFlight.departure.airportId : null
    case Direction.R:
      return !!inboundFlight && !!('arrival' in inboundFlight) ? inboundFlight.arrival.airportId : null
    default:
      return null
  }
}

export const mapGateways = (fetchedGateways: Gateway[]) => {
  const mappedGateways: IMappedGateway[] = []

  if (!fetchedGateways) return []

  const init = (gateway: Gateway) => {
    const mappedGateway: IMappedGateway = {
      availableFlightDirections: !!gateway.flightClass?.length ? [Direction.RT, Direction.O, Direction.R] : [],
      availableFlightClasses: mapFlightClasses(gateway.flightClass),
      locCd: gateway.airportCode ?? '',
      locName: gateway.airportName ?? '',
    }

    return mappedGateway
  }

  const mapFlightClasses = (items?: DictionaryItem[]) => {
    const array: FlightClass[] = []

    if (!items?.length) return []

    for (const item of items) {
      const code = item.code as FlightClass

      if (array.includes(code)) continue

      array.push(code)
    }

    return array
  }

  for (const gateway of fetchedGateways) {
    if (!gateway.flightClass?.length) continue

    let airport = mappedGateways.find(airport => airport.locCd === gateway.airportCode)

    if (!airport) {
      airport = init(gateway)
      mappedGateways.push(airport)
    }
  }

  return mappedGateways
}

export const getAirlineNames = (flight: IOutboundInboundFlexFlight | IExpinetFlightType) => {
  const mainFlightAirline = flight.mainFlight.airline
  const feederFlightAirline = flight.feederFlight?.airline
  let airlineNames = `${mainFlightAirline.name}`

  if (!!feederFlightAirline?.name && mainFlightAirline.id !== feederFlightAirline.id) {
    airlineNames += '/ ' + feederFlightAirline.name
  }

  return airlineNames
}

export const getAirlineArray = (flight: Flight, direction: Direction) => {
  const airlines: IAirline[] = []

  if (!flight.flightSegments?.length) return []

  for (const connection of flight.flightSegments) {
    if (!connection.carrierNumber || connection.direction !== direction) continue

    const airline: IAirline = {
      name: connection.carrierName,
      id: connection.carrierCode,
    }

    if (!airlines.find(alreadyAddedAirline => alreadyAddedAirline.id === airline.id)) {
      airlines.push(airline)
    }
  }

  return airlines
}

export const AddError = (origin?: string) => {
  const originName = `flexflight - ${origin}`
  handleError({ error: new Error('Flight connection is null'), origin: originName })
}

export const getDummyBirthdates = (paxConfig: IPaxConfig, birthdates?: string[]): string[] => {
  const paxAges: string[] = []

  for (let i = 0; i < paxConfig.adults; ++i) {
    paxAges.push(dayjs().subtract(40, 'year').format(ISO_DATE_FORMAT))
  }

  for (let j = 0; j < paxConfig.juveniles; ++j) {
    paxAges.push(dayjs().subtract(18, 'year').format(ISO_DATE_FORMAT))
  }

  for (let k = 0; k < paxConfig.children + paxConfig.babies; ++k) {
    if (!birthdates?.length || k > Object.values(birthdates).length) break

    const childsBirthdate = dayjs(Object.values(birthdates)[k], DEFAULT_DATE_FORMAT).format(ISO_DATE_FORMAT)

    paxAges.push(childsBirthdate)
  }

  return paxAges
}

export const displayFlightDirection = (direction: string) => {
  switch (direction) {
    case Direction.RT: {
      return 'Hin- und Rückflug'
    }
    case Direction.R: {
      return 'Rückflug'
    }
    case Direction.O: {
      return 'Hinflug'
    }
    default:
      return ''
  }
}

export const flexFlightHelper = {
  AddError,
  initFlights,
  findFilterKey,
  makeOneWayFlexFlight,
  makeTwoWayFlexFlight,
}

// ---------------- Export for testing purposes only ----------------

export const exportForTesting = {
  valuesAreEqual,
  getAirlineArray,
}
